import { Box, Paper } from "@material-ui/core"
import { DayPicker } from "./DayPicker"
import { ProjectSelector } from "../ProjectSelector"
import { TaskSelector } from "../TaskSelector/TaskSelector"
import { MethodSelectorTabs } from "./MethodSelectorTabs"
import { ManualEntry } from "./ManualEntry"
import { IntervalEntry } from "./IntervalEntry"
import { DescriptionInput } from "./DescriptionInput"
import { FormActions } from "./FormActions"
import { HourReportFormState } from "../../schemas"
import { FormikProps } from "formik"
import { TimerEntry } from "./TimerEntry"
import { HourReportFormMembershipOption, HourReportFormProjectOption } from "../../api/schemas"
import { MembershipSelector } from "../MembershipSelector"
import type { HourReportingWorkflowSettings } from "../../hooks/use-hour-reporting-workflow-settings"

export const HourReportForm = ({
  formik,
  saveAsDraft,
  submitReport,
  projectOptions,
  membershipOptions,
  isSubmitting = formik.isSubmitting,
  showSaveAsDraft = true,
  showDayPicker = true,
  workflowSettings,
  onDateChange,
  onMembershipChange,
}: HourReportFormProps) => {
  const { values } = formik

  const handleChangeProject = (projectId: string | null) => {
    formik.setFieldValue("projectId", projectId)
    formik.setFieldValue("taskId", undefined) // Reset task when project changes
  }

  const handleChangeTask = (taskId: string | null) => {
    formik.setFieldValue("taskId", taskId)
  }

  return (
    <Paper variant="outlined" elevation={0} style={{ padding: 16 }}>
      <form onSubmit={formik.handleSubmit}>
        <Box display="flex" flexDirection="column" marginRight={3}>
          {showDayPicker ? <DayPicker formik={formik} onDateChange={onDateChange} /> : null}

          <MembershipSelector
            selectedMembershipId={values.membershipId ?? null}
            onChange={(membershipId) => {
              formik.setFieldValue("membershipId", membershipId)
              onMembershipChange?.(membershipId)
            }}
            membershipOptions={membershipOptions}
          />
          <ProjectSelector
            selectedProjectId={values.projectId ?? null}
            projectOptions={projectOptions}
            onChange={handleChangeProject}
          />
          {values.projectId ? (
            <TaskSelector
              projectId={values.projectId}
              selectedTaskId={values.taskId ?? null}
              onChange={handleChangeTask}
            />
          ) : null}

          <MethodSelectorTabs formik={formik} />

          {values.method === "manual" ? <ManualEntry formik={formik} /> : null}
          {values.method === "interval" ? <IntervalEntry formik={formik} /> : null}
          {values.method === "timer" ? <TimerEntry formik={formik} /> : null}

          <DescriptionInput formik={formik} />

          <FormActions
            onSaveAsDraft={saveAsDraft}
            onSubmitReport={submitReport}
            isSubmitting={isSubmitting}
            showSaveAsDraft={showSaveAsDraft}
            supportsSubmission={workflowSettings?.supportsSubmission}
            supportsChecking={workflowSettings?.supportsChecking}
            supportsApproval={workflowSettings?.supportsApproval}
          />
        </Box>
      </form>
    </Paper>
  )
}

type HourReportFormProps = {
  formik: FormikProps<HourReportFormState>
  saveAsDraft: () => void
  submitReport: () => void
  projectOptions: HourReportFormProjectOption[]
  membershipOptions: HourReportFormMembershipOption[] // Optional if not used
  isSubmitting?: boolean
  showSaveAsDraft?: boolean
  showDayPicker?: boolean
  workflowSettings?: HourReportingWorkflowSettings
  onDateChange?: (date: Date) => void
  onMembershipChange?: (membershipId: string | null) => void
}
